import { create } from 'zustand';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { uid } from '../../store/persist';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => string;
  dismiss: (id: string) => void;
}

export const useToasts = create<ToastState>()((set, get) => ({
  toasts: [],
  push: ({ duration = 4000, ...rest }) => {
    const id = uid('toast');
    set({ toasts: [...get().toasts, { ...rest, id, duration }] });
    if (duration > 0) {
      setTimeout(() => get().dismiss(id), duration);
    }
    return id;
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

export const toast = {
  info: (title: string, message?: string) =>
    useToasts.getState().push({ tone: 'info', title, message }),
  success: (title: string, message?: string) =>
    useToasts.getState().push({ tone: 'success', title, message }),
  warning: (title: string, message?: string) =>
    useToasts.getState().push({ tone: 'warning', title, message }),
  error: (title: string, message?: string) =>
    useToasts.getState().push({ tone: 'error', title, message }),
};

const TONE_STYLES: Record<ToastTone, { border: string; icon: typeof Info; color: string }> = {
  info: { border: 'border-sky-450/40', icon: Info, color: 'text-sky-450' },
  success: { border: 'border-agave-500/40', icon: CheckCircle2, color: 'text-agave-400' },
  warning: { border: 'border-gold-500/40', icon: AlertTriangle, color: 'text-gold-400' },
  error: { border: 'border-crimson-500/40', icon: XCircle, color: 'text-crimson-500' },
};

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-200 flex flex-col items-center gap-2 p-4 sm:items-end">
      <AnimatePresence>
        {toasts.map((t) => {
          const style = TONE_STYLES[t.tone];
          const Icon = style.icon;
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              className={cn(
                'panel pointer-events-auto flex w-full max-w-sm items-start gap-3 border p-3.5',
                style.border,
              )}
              role="status"
            >
              <Icon className={cn('mt-0.5 size-5 shrink-0', style.color)} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">{t.title}</p>
                {t.message ? <p className="mt-0.5 text-xs text-ink-300">{t.message}</p> : null}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="text-ink-400 transition-colors hover:text-white"
                aria-label="Dismiss notification"
              >
                <X className="size-4" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
